import { Type } from "@sinclair/typebox";
import { ApiError, Problems } from "@zuplo/errors";
import { Hono } from "hono";
import type { EditorPreferencesPort } from "../ports.js";
import { normalizeRelativePath } from "../utils/path.js";
import { validate } from "../validation.js";

// Supported editors with their URL protocols
interface EditorInfo {
  name: string;
  protocol: string;
  urlTemplate: string;
  detectFiles?: string[];
}

const EDITORS: Record<string, EditorInfo> = {
  vscode: {
    name: "Visual Studio Code",
    protocol: "vscode",
    urlTemplate: "vscode://file{file}:{line}:{column}",
    detectFiles: [".vscode/settings.json"],
  },
  cursor: {
    name: "Cursor",
    protocol: "cursor",
    urlTemplate: "cursor://file{file}:{line}:{column}",
    detectFiles: [".cursor"],
  },
  webstorm: {
    name: "WebStorm",
    protocol: "webstorm",
    urlTemplate: "webstorm://open?file={file}&line={line}",
    detectFiles: [".idea"],
  },
  phpstorm: {
    name: "PhpStorm",
    protocol: "phpstorm",
    urlTemplate: "phpstorm://open?file={file}&line={line}",
    detectFiles: [".idea"],
  },
  intellij: {
    name: "IntelliJ IDEA",
    protocol: "idea",
    urlTemplate: "idea://open?file={file}&line={line}",
    detectFiles: [".idea"],
  },
  sublimetext: {
    name: "Sublime Text",
    protocol: "subl",
    urlTemplate: "subl://open?url=file://{file}&line={line}&column={column}",
  },
};

interface EditorRoutesOptions {
  /**
   * Where the project lives on the machine running the editor, used to build the
   * absolute path in the `vscode://`-style deep link.
   *
   * The preview Worker has no such directory and passes the notional root it
   * reports elsewhere. The resulting link won't resolve on a visitor's machine,
   * since nothing on their disk corresponds to it.
   */
  sourceDirectory: string;
  preferences: EditorPreferencesPort;
}

const EditorPreferenceBody = Type.Object({
  editor: Type.String(),
});

const OpenEditorBody = Type.Object({
  file: Type.String(),
  line: Type.Optional(Type.Number()),
  column: Type.Optional(Type.Number()),
});

export function createEditorRoutes(options: EditorRoutesOptions): Hono {
  const { sourceDirectory, preferences } = options;
  const app = new Hono();

  // Detect editor: GET /editor/detect
  app.get("/detect", async (c) => {
    // Check saved preference
    const savedEditor = await preferences.get();
    if (savedEditor && EDITORS[savedEditor]) {
      return c.json({
        editor: savedEditor,
        name: EDITORS[savedEditor].name,
      });
    }

    // No editor found
    return c.json({
      editor: null,
      name: null,
    });
  });

  // Get available editors: GET /editor/list
  app.get("/list", (c) => {
    const editors = Object.entries(EDITORS).map(([id, info]) => ({
      id,
      name: info.name,
    }));
    return c.json({ editors });
  });

  // Save editor preference: POST /editor/preference
  app.post("/preference", validate("json", EditorPreferenceBody), async (c) => {
    const { editor } = c.req.valid("json");

    if (!EDITORS[editor]) {
      throw new ApiError({
        ...Problems.BAD_REQUEST,
        detail: `Unknown editor: ${editor}`,
      });
    }

    await preferences.set(editor);
    return c.json({ success: true });
  });

  // Get URL to open file in editor: POST /editor/open
  app.post("/open", validate("json", OpenEditorBody), async (c) => {
    const { file, line = 1, column = 1 } = c.req.valid("json");

    if (!file) {
      throw new ApiError({
        ...Problems.BAD_REQUEST,
        detail: "File path is required",
      });
    }

    // Get editor preference
    const savedEditor = await preferences.get();
    if (!savedEditor || !EDITORS[savedEditor]) {
      throw new ApiError({
        ...Problems.BAD_REQUEST,
        detail: "No editor preference set",
      });
    }

    const editor = EDITORS[savedEditor];

    // Build file path. Joined by hand rather than with `node:path` so this route
    // runs unchanged in a Worker; the portal only sends slash-separated paths,
    // and every deep-link protocol above accepts them.
    //
    // The trailing separators are trimmed in a loop rather than with a regex:
    // an anchored `/[/\\]+$/` backtracks quadratically over a long run of
    // slashes, and `sourceDirectory` reaches here from an exported function's
    // argument (CodeQL flags exactly this as polynomial redos).
    let projectRoot = sourceDirectory;
    while (projectRoot.endsWith("/") || projectRoot.endsWith("\\")) {
      projectRoot = projectRoot.slice(0, -1);
    }

    const relativePath = normalizeRelativePath(file);
    const fullPath = `${projectRoot}/${relativePath}`;

    // Build URL from template
    const url = editor.urlTemplate
      .replace("{file}", fullPath)
      .replace("{line}", String(line))
      .replace("{column}", String(column));

    return c.json({ url });
  });

  return app;
}
